import React, { Fragment, useContext, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import LocalPharmacyOutlinedIcon from "@mui/icons-material/LocalPharmacyOutlined";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import "../../../../assets/css/DataTableDemo.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "../../../../assets/css/PrimeGrid.css";

import { saveAs } from "file-saver";

import { AlertContext } from "../../../../context/AlertContext";
import { AuthContext } from "../../../../context/AuthContext";
import { ConfiguracionContext } from "../../../../context/ConfiguracionContext";
import { getBrand, createBrandTheme } from "../../../../theme/brand";
import Spinner from "../../Spinner";
import Barra from "../../Barra";

const exportColumns = [
  { title: "ID", dataKey: "id" },
  { title: "SKU", dataKey: "sku" },
  { title: "Fecha", dataKey: "fecha" },
  { title: "Cantidad", dataKey: "cantidad" },
  { title: "Almacen", dataKey: "almacen" },
  { title: "Tipo", dataKey: "tipo" },
  { title: "Descripción", dataKey: "descripcion" },
];

export default function ConsumosMedicamentos() {
  const [globalFilter, setGlobalFilter] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [consumoMedicamento, guardarConsumoMedicamento] = useState(null);
  const [stockTotal, guardarStockTotal] = useState(null);
  const [visible, setVisible] = useState(false);
  const [almacenSel, setAlmacenSel] = useState(null); // valor controlado del Autocomplete
  const toast = useRef(null);
  const navigate = useNavigate();

  const { mensajeAlerta } = useContext(AlertContext);
  const { state } = useContext(AuthContext);
  const {
    statusAcceso,
    almacenes,
    almacen,
    configuracion,
    consumosMedicamentos,
    stocksMedicamentos,
    movimientosMedicamentos,
    obtenerConsumosMedicamentos,
    obtenerAcceso,
    obtenerAlmacenes,
    guardarAlmacen,
    guardarConsumosMedicamentos,
    obtenerStocksMedicamentos,
    agregarReposicionMedicamentos,
  } = useContext(ConfiguracionContext);

  const theme = createBrandTheme(getBrand(configuracion));

  useEffect(() => {
    if (state.usuario) {
      obtenerAcceso({ usuarioId: state.usuario.id, layout: "ConsumosMedicamentos" });
    }
    obtenerAlmacenes();
    obtenerStocksMedicamentos({ almacen: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.id]);

  useEffect(() => {
    if (statusAcceso === 400) {
      mensajeAlerta(
        "Accesos",
        "No tiene permiso para acceder a esta pantalla !.",
        "error"
      );
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusAcceso]);

  const seleccionarAlmacen = (value) => {
    setAlmacenSel(value);
    if (value) {
      obtenerConsumosMedicamentos({ almacen: value.id });
      guardarAlmacen({ id: value.id, nombre: value.nombre });
    } else {
      guardarConsumosMedicamentos(null);
      guardarAlmacen(null);
    }
  };

  const addClick = () => {
    setSelectedRow(null);
    guardarConsumoMedicamento({ descripcion: null });
    setVisible(true);
  };

  const onHide = () => {
    setSelectedRow(null);
    setVisible(false);
  };

  const abrirReposicion = (row) => {
    setSelectedRow(row);
    guardarConsumoMedicamento(row.reponer);
    const s =
      stocksMedicamentos && stocksMedicamentos.find((x) => x.sku === row.sku);
    guardarStockTotal(s ? s.stock : 0);
    setVisible(true);
  };

  const aceptarClick = () => {
    if (
      consumoMedicamento === undefined ||
      consumoMedicamento === null ||
      consumoMedicamento === ""
    ) {
      mensajeAlerta("Reposición de Medicamentos", "La cantidad es necesaria !.", "error");
      return;
    }

    if (selectedRow) {
      const index = consumosMedicamentos.findIndex(
        (item) => item.sku === selectedRow.sku
      );
      if (index >= 0) {
        consumosMedicamentos[index].reponer = Number(consumoMedicamento);
      }
      onHide();
    }
  };

  const confirmarClick = () => {
    const date = new Date();
    const newDate = new Date(date);
    newDate.setHours(date.getHours() - 3);

    const movimientoMedicamento = [];
    for (let i = 0; i < consumosMedicamentos.length; i++) {
      if (Number(consumosMedicamentos[i].reponer) > 0) {
        movimientoMedicamento.push({
          id: null,
          sku: consumosMedicamentos[i].sku,
          fecha: newDate,
          cantidad: consumosMedicamentos[i].reponer,
          almacenId: almacen.id,
          tipo: "E",
          descripcion: almacen.nombre,
          servicio: null,
        });
      }
    }

    // reset del almacén elegido (antes se hacía clickeando el botón "clear" por DOM)
    seleccionarAlmacen(null);

    if (movimientoMedicamento.length > 0) {
      agregarReposicionMedicamentos({
        movimientoMedicamento,
        ultimoId: movimientosMedicamentos[0].id2,
      });
    } else {
      toast.current.show({
        severity: "info",
        summary: "Reposición",
        detail: "No hay medicamentos para reponer !.",
        life: 3000,
      });
    }
  };

  const exitClick = () => navigate("/");

  const exportPdf = () => {
    import("jspdf").then((jsPDF) => {
      import("jspdf-autotable").then(() => {
        const doc = new jsPDF.default(0, 0);
        doc.setFontSize(12);
        doc.text("Listado de Movimientos de Medicamentos", 60, 8);
        doc.autoTable(exportColumns, consumosMedicamentos || []);
        window.open(doc.output("bloburl"));
      });
    });
  };

  const exportExcel = () => {
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(consumosMedicamentos || []);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });
      saveAs(blob, `movimientosdemedicamentos_export_${new Date().getTime()}.xlsx`);
    });
  };

  const subtitulo =
    movimientosMedicamentos && movimientosMedicamentos.length > 0
      ? `Desde ${movimientosMedicamentos[0].fecha1} hasta ${movimientosMedicamentos[0].fecha2}`
      : "Consumos de medicamentos";

  const cargando = statusAcceso === 0 || statusAcceso === 400;

  return (
    <Fragment>
      <Barra />
      <Toast ref={toast} />

      {cargando ? <Spinner /> : null}

      {statusAcceso === 200 ? (
        <ThemeProvider theme={theme}>
          <Box id="dataTableBotones" sx={{ px: { xs: 1, sm: 2 }, pb: 3 }}>
            {almacenes ? (
              <Fragment>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ xs: "stretch", md: "center" }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                        }}
                      >
                        <LocalPharmacyOutlinedIcon />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          Consumo de Medicamentos
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {subtitulo}
                        </Typography>
                      </Box>
                    </Stack>

                    <Autocomplete
                      options={almacenes.filter((item) => item.id !== 1)}
                      value={almacenSel}
                      disablePortal
                      getOptionLabel={(o) => (o && o.nombre) || ""}
                      isOptionEqualToValue={(o, v) => o.id === (v && v.id)}
                      onChange={(e, value) => seleccionarAlmacen(value)}
                      sx={{ minWidth: { xs: "100%", md: 260 } }}
                      renderInput={(params) => (
                        <TextField {...params} label="Almacén" size="small" />
                      )}
                    />

                    <TextField
                      size="small"
                      placeholder="Buscar…"
                      onInput={(e) => setGlobalFilter(e.target.value)}
                      sx={{ minWidth: { xs: "100%", md: 200 } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={addClick}>
                      Nuevo
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckIcon />}
                      onClick={confirmarClick}
                      disabled={!consumosMedicamentos || consumosMedicamentos.length === 0}
                    >
                      Confirmar Reposición
                    </Button>
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<FileDownloadOutlinedIcon />}
                      onClick={exportExcel}
                    >
                      Excel
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<PictureAsPdfOutlinedIcon />}
                      onClick={exportPdf}
                    >
                      PDF
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                      variant="text"
                      color="inherit"
                      startIcon={<CloseIcon />}
                      onClick={exitClick}
                    >
                      Cerrar
                    </Button>
                  </Stack>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <DataTable
                    value={consumosMedicamentos || []}
                    selection={selectedRow}
                    onSelectionChange={(e) => abrirReposicion(e.value)}
                    selectionMode="single"
                    dataKey="id"
                    className="p-datatable-sm p-datatable-striped p-datatable-responsive-demo mito-grid"
                    globalFilter={globalFilter}
                    emptyMessage="Elegí un almacén para ver los consumos."
                    paginator
                    paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
                    rows={10}
                    rowsPerPageOptions={[10, 20, 50]}
                  >
                    <Column field="sku" header="SKU" sortable style={{ width: "10rem" }} />
                    <Column field="nombre" header="Nombre" sortable />
                    <Column field="cantidad" header="Consumido" sortable style={{ width: "9rem" }} />
                    <Column field="stock" header="Stock" sortable style={{ width: "8rem" }} />
                    <Column field="reponer" header="Reponer" sortable style={{ width: "8rem" }} />
                  </DataTable>
                </Paper>
              </Fragment>
            ) : (
              <Spinner />
            )}
          </Box>

          <Dialog open={visible} onClose={onHide} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700 }}>
              <LocalPharmacyOutlinedIcon color="primary" />
              {selectedRow
                ? `Reposición de ${selectedRow.nombre}`
                : "Reposición de Medicamentos"}
            </DialogTitle>
            <DialogContent dividers>
              {selectedRow ? (
                <Fragment>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {`Total en stock: ${stockTotal}`}
                  </Typography>
                  <TextField
                    label="Cantidad"
                    type="number"
                    fullWidth
                    size="small"
                    autoFocus
                    InputLabelProps={{ shrink: true }}
                    value={consumoMedicamento ?? ""}
                    onChange={(e) => {
                      let v = e.target.value;
                      if (!v) {
                        guardarConsumoMedicamento(0);
                        return;
                      }
                      if (Number(v) < 0) v = "0";
                      if (Number(v) > stockTotal) {
                        v = String(stockTotal);
                        toast.current.show({
                          severity: "info",
                          summary: "Stocks",
                          detail: `La cantidad ha superado el stock de farmacia (${stockTotal}).`,
                          life: 3000,
                        });
                      }
                      guardarConsumoMedicamento(v);
                    }}
                  />
                </Fragment>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Seleccioná una fila para reponer.
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={onHide} color="inherit" startIcon={<CloseIcon />}>
                Cancelar
              </Button>
              <Button onClick={aceptarClick} variant="contained" startIcon={<CheckIcon />}>
                Aceptar
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
      ) : null}
    </Fragment>
  );
}
